import { mountRestockEventProvider } from './restock-event-provider.js';

const PUBLIC_ID = /^[A-Za-z0-9]{6}$/;
const VARIANT_ID = /^[1-9][0-9]{0,19}$/;
const E164 = /^\+[1-9][0-9]{7,14}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REVISION = '2026-10-15';

export class RestockRequestError extends Error {
  constructor(code, retryAfterMs = 0, progress = {}) { super(code); this.code = code; this.retryAfterMs = retryAfterMs; this.progress = progress; }
}
export function buildRestockPayload({ variantId, channel, channels, email, phone }) {
  if (!VARIANT_ID.test(String(variantId || ''))) throw new RestockRequestError('unavailable');
  const requested = channels || [channel];
  if (!Array.isArray(requested) || !requested.length || requested.some(value => !['EMAIL','SMS'].includes(value))) throw new RestockRequestError('choose_channel');
  const selected = [...new Set(requested)], attributes = {};
  if (selected.includes('EMAIL')) {
    if (!EMAIL.test(String(email || '').trim())) throw new RestockRequestError('invalid');
    attributes.email = String(email).trim();
  }
  if (selected.includes('SMS')) {
    if (!E164.test(String(phone || '').trim())) throw new RestockRequestError('invalid');
    attributes.phone_number = String(phone).trim();
  }
  return { data: { type: 'back-in-stock-subscription', attributes: { channels: selected, profile: { data: { type: 'profile', attributes } } }, relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } } } };
}
export function buildRestockSmsConsent(phone, consent, listId) {
  if (consent !== true || !E164.test(String(phone || '').trim())) throw new RestockRequestError('sms_consent_required');
  if (!PUBLIC_ID.test(listId || '')) throw new RestockRequestError('unavailable');
  return { data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { phone_number: String(phone).trim(), subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } } } }, custom_source: 'No Limit Vintage stock notification request' }, relationships: { list: { data: { type: 'list', id: listId } } } } };
}
export function restockRetryDelay(value, now = Date.now()) {
  const raw = String(value || '').trim();
  const milliseconds = /^\d+(?:\.\d+)?$/.test(raw) ? Number(raw) * 1000 : Date.parse(raw) - now;
  return Math.min(300000, Math.max(1000, Number.isFinite(milliseconds) ? milliseconds : 30000));
}
async function postClient(path, payload, { siteId, fetch: fetcher, signal }) {
  const response = await fetcher(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(siteId)}`, {
    method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal,
    headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: REVISION }, body: JSON.stringify(payload)
  });
  // The official client endpoint returns an empty 202, never a subscription object.
  if (response.status === 202) return;
  if (response.status === 429) throw new RestockRequestError('rate_limit', restockRetryDelay(response.headers?.get('Retry-After')));
  if (response.status === 404) throw new RestockRequestError('unavailable');
  throw new RestockRequestError('error');
}
export async function submitRestockRequest(config, values, dependencies = {}) {
  if (config.enabled !== true || !PUBLIC_ID.test(config.siteId || '') || !VARIANT_ID.test(String(values.variantId || ''))) throw new RestockRequestError('unavailable');
  const email = values.emailSelected === true, sms = values.smsSelected === true;
  if (!email && !sms) throw new RestockRequestError('choose_channel');
  if (sms && (config.smsEnabled !== true || values.smsConsent !== true)) throw new RestockRequestError('sms_consent_required');
  // Validate all requested fields before making even the first network request.
  const stockPayload = buildRestockPayload({ variantId: values.variantId, channels: [email ? 'EMAIL' : null, sms ? 'SMS' : null].filter(Boolean), email: values.email, phone: values.phone });
  const consentPayload = sms ? buildRestockSmsConsent(values.phone, values.smsConsent, config.smsListId) : null;
  const options = { siteId: config.siteId, fetch: dependencies.fetch || globalThis.fetch, signal: dependencies.signal };
  const progress = { emailReceived: false, smsReceived: false, smsConsentRecorded: false };
  try {
    if (consentPayload) {
      if (dependencies.smsConsentAlreadyRecorded !== true) await postClient('subscriptions', consentPayload, options);
      progress.smsConsentRecorded = true;
    }
    // Klaviyo keeps the first profile+variant subscription. Queue all selected
    // channels together rather than losing the second channel to deduplication.
    await postClient('back-in-stock-subscriptions', stockPayload, options);
    progress.emailReceived = email; progress.smsReceived = sms;
    return progress;
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    const failure = error instanceof RestockRequestError ? error : new RestockRequestError('error');
    failure.progress = progress;
    throw failure;
  }
}

export function syncRestockVariant(trigger, select) {
  const option = select?.options?.[select.selectedIndex];
  if (!trigger || !option) return;
  trigger.dataset.restockVariantId = String(option.value || '');
  trigger.dataset.restockVariantTitle = option.dataset?.restockTitle || option.dataset?.title || option.textContent || '';
  trigger.hidden = option.dataset?.available !== 'false';
}
function copyFrom(document) {
  return Object.fromEntries([...document.querySelector('[data-restock-copy]')?.content?.querySelectorAll('[data-copy]') || []].map(node => [node.dataset.copy, node.textContent]));
}
function configFrom(document) {
  try { return JSON.parse(document.querySelector('[data-restock-config]')?.content?.textContent || '{}'); } catch { return {}; }
}
export function mountRestockNotifications(document, { window: win = document.defaultView, config = configFrom(document), copy = copyFrom(document), fetch: fetcher } = {}) {
  const dialog = document.querySelector('[data-restock-dialog]');
  if (!dialog || !win || dialog.dataset.restockReady === 'true') return null;
  const form = dialog.querySelector('[data-restock-form]'), closeButton = dialog.querySelector('[data-restock-close]');
  if (!form || !closeButton) return null;
  dialog.dataset.restockReady = 'true';
  const find = name => dialog.querySelector(`[data-restock-${name}]`);
  const controls = Object.fromEntries(['heading','description','item','preview','fields','email-choice','sms-choice','sms-unready','email-field','phone-field','consent-field','email','phone','sms-consent','email-note','submit','status'].map(key => [key, find(key)]));
  const abort = new (win.AbortController || AbortController)(), options = { signal: abort.signal };
  let current = null, requestAbort, overflow = '', busy = false, cooldown = 0, retryAt = 0, destroyed = false;
  const text = key => copy[key] || key;
  const tell = message => { controls.status.textContent = message; controls.status.hidden = !message; };
  const eventFormId = () => controls['sms-choice'].checked ? config.eventSmsFormId : config.eventEmailFormId;
  const eventProvider = () => {
    const provider = win.NLVRestockEventProvider, formId = eventFormId();
    if (controls['sms-choice'].checked && config.smsEnabled !== true) return null;
    return config.eventEnabled === true && PUBLIC_ID.test(formId || '') && typeof provider?.open === 'function' ? { provider, formId } : null;
  };
  const ready = () => current?.mode === 'event' ? Boolean(eventProvider()) : config.enabled === true && PUBLIC_ID.test(config.siteId || '') && VARIANT_ID.test(current?.variantId || '');
  const setContactState = () => {
    if (!current) return;
    const enabled = ready(), event = current.mode === 'event';
    const email = controls['email-choice'].checked, sms = controls['sms-choice'].checked;
    controls.preview.hidden = enabled;
    dialog.setAttribute('aria-describedby', enabled ? 'RestockDescription' : 'RestockDescription RestockPreview');
    const smsUnavailable = !event && enabled && (config.smsEnabled !== true || !PUBLIC_ID.test(config.smsListId || '')) || event && config.eventEnabled === true && (config.smsEnabled !== true || !PUBLIC_ID.test(config.eventSmsFormId || ''));
    controls.fields.disabled = busy; controls['email-choice'].disabled = busy; controls['sms-choice'].disabled = busy || smsUnavailable;
    if (smsUnavailable) controls['sms-choice'].checked = false;
    controls['sms-unready'].hidden = !smsUnavailable;
    const showEmail = email && !(event && enabled), showSms = sms && !smsUnavailable && !(event && enabled);
    controls['email-field'].hidden = !showEmail; controls.email.required = showEmail; controls.email.disabled = busy || !showEmail;
    controls['phone-field'].hidden = !showSms; controls.phone.required = showSms; controls.phone.disabled = busy || !showSms;
    controls['consent-field'].hidden = !showSms; controls['sms-consent'].required = showSms; controls['sms-consent'].disabled = busy || !showSms;
    controls['email-note'].hidden = !showEmail || event;
    controls.submit.disabled = !enabled || busy || Date.now() < retryAt;
    controls.submit.textContent = text(busy ? 'sending' : !enabled ? 'preview_submit' : event ? 'continue' : 'submit');
  };
  const reset = () => {
    requestAbort?.abort(); requestAbort = undefined; busy = false;
    form.reset(); controls.email.value = ''; controls.phone.value = ''; controls['sms-consent'].checked = false;
    controls.fields.disabled = false; controls['email-choice'].disabled = false; controls['sms-choice'].disabled = false; tell('');
  };
  const closed = () => {
    if (dialog.open || !current) return;
    const trigger = current?.trigger; reset(); current = null;
    trigger?.setAttribute('aria-expanded', 'false');
    const another = document.querySelector('dialog[open]');
    document.body.style.overflow = another ? 'hidden' : overflow;
    if (!another && trigger?.isConnected) trigger.focus({ preventScroll: true });
  };
  const close = () => { if (dialog.open) dialog.close(); closed(); };
  const open = trigger => {
    const mode = ['product','premier','event'].includes(trigger?.dataset.restockContext) ? trigger.dataset.restockContext : 'product';
    if (!trigger || destroyed || trigger.hidden || trigger.disabled) return false;
    if (dialog.open) close();
    document.querySelectorAll('dialog[open]').forEach(other => other.close());
    overflow = document.body.style.overflow;
    reset(); current = { trigger, mode, variantId: trigger.dataset.restockVariantId || '' };
    controls.heading.textContent = text(`title_${mode}`); controls.description.textContent = text(`description_${mode}`);
    const title = trigger.dataset.restockProductTitle || '', variant = trigger.dataset.restockVariantTitle || '';
    controls.item.textContent = [title, variant && variant !== 'Default Title' ? variant : ''].filter(Boolean).join(' · '); controls.item.hidden = mode === 'event' || !title;
    controls['email-choice'].type = mode === 'event' ? 'radio' : 'checkbox'; controls['sms-choice'].type = mode === 'event' ? 'radio' : 'checkbox';
    controls['email-choice'].name = mode === 'event' ? 'restock-event-channel' : ''; controls['sms-choice'].name = mode === 'event' ? 'restock-event-channel' : '';
    controls['email-choice'].checked = true; controls['sms-choice'].checked = false; setContactState();
    if (Date.now() < retryAt) tell(text('rate_limit'));
    try { dialog.showModal(); } catch { current = null; return false; }
    trigger.setAttribute('aria-expanded', 'true'); document.body.style.overflow = 'hidden'; controls.heading.focus({ preventScroll: true }); return true;
  };
  const watch = scope => {
    const buttons = [...scope.querySelectorAll('[data-restock-select-id]')];
    if (scope.matches?.('[data-restock-select-id]')) buttons.unshift(scope);
    for (const trigger of buttons) syncRestockVariant(trigger, document.getElementById(trigger.dataset.restockSelectId));
  };
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-restock-open]');
    if (!trigger || event.button && event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); open(trigger);
  }, options);
  document.addEventListener('change', event => {
    if (!event.target?.id) return;
    for (const trigger of document.querySelectorAll('[data-restock-select-id]')) {
      if (trigger.dataset.restockSelectId !== event.target.id) continue;
      syncRestockVariant(trigger, event.target);
      // Never submit the previous variant while a different option is selected.
      if (current?.trigger === trigger) close();
    }
  }, options);
  document.addEventListener('shopify:section:load', event => watch(event.target), options);
  document.addEventListener('shopify:section:unload', event => { if (current?.trigger && event.target.contains(current.trigger)) close(); }, options);
  closeButton.addEventListener('click', close, options);
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, options);
  dialog.addEventListener('close', closed, options);
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  }, options);
  form.addEventListener('change', () => { tell(''); setContactState(); }, options);
  controls.phone.addEventListener('input', () => { controls['sms-consent'].checked = false; }, options);
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (!current || busy || !ready() || Date.now() < retryAt) return;
    if (current.mode === 'event') {
      const adapter = eventProvider();
      if (!adapter) return;
      const trigger = current.trigger, channel = controls['sms-choice'].checked ? 'sms' : 'email';
      close();
      try { const opened = await adapter.provider.open({ formId: adapter.formId, channel, source: 'fill-a-bag-preorders', returnFocus: trigger }); if (opened === false) throw new Error('unavailable'); }
      catch { if (!destroyed && !current && !document.querySelector('dialog[open]') && trigger.isConnected && open(trigger)) tell(text('unavailable')); }
      return;
    }
    if (!controls['email-choice'].checked && !controls['sms-choice'].checked) { tell(text('choose_channel')); return; }
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const session = current, variantId = session.variantId;
    const submitted = Object.freeze({ variantId, emailSelected: controls['email-choice'].checked, smsSelected: controls['sms-choice'].checked, email: controls.email.value.trim(), phone: controls.phone.value.trim(), smsConsent: controls['sms-consent'].checked });
    requestAbort = new (win.AbortController || AbortController)(); busy = true; tell(''); setContactState();
    try {
      await submitRestockRequest(config, submitted, { fetch: fetcher || win.fetch.bind(win), signal: requestAbort.signal, smsConsentAlreadyRecorded: session.smsConsentPhone === submitted.phone });
      if (current !== session || !dialog.open) return;
      controls.email.value = ''; controls.phone.value = ''; controls['sms-consent'].checked = false; tell(text('success'));
      controls.submit.disabled = true;
    } catch (error) {
      if (error?.name === 'AbortError' || current !== session || !dialog.open) return;
      const progress = error.progress || {};
      if (progress.smsConsentRecorded) session.smsConsentPhone = submitted.phone;
      const code = progress.smsConsentRecorded ? 'partial' : ['rate_limit','unavailable','choose_channel','sms_consent_required'].includes(error.code) ? error.code : 'error';
      tell(text(code));
      if (error.retryAfterMs) { retryAt = Date.now() + error.retryAfterMs; cooldown = win.setTimeout(() => { retryAt = 0; setContactState(); }, error.retryAfterMs); }
    } finally {
      if (current === session) {
        busy = false; requestAbort = undefined;
        if (dialog.open && controls.status.textContent !== text('success')) setContactState();
      }
    }
  }, options);
  win.addEventListener('pagehide', close, options); watch(document);
  return { open, close, destroy() { if (destroyed) return; close(); reset(); win.clearTimeout(cooldown); cooldown = 0; retryAt = 0; destroyed = true; abort.abort(); delete dialog.dataset.restockReady; } };
}
if (typeof document !== 'undefined') {
  mountRestockEventProvider(document, { config: configFrom(document), copy: copyFrom(document) });
  mountRestockNotifications(document);
}
